import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useParams, useNavigate } from 'react-router-dom';
import { Row, Col, Button } from 'react-bootstrap5';
import * as Yup from 'yup';
import { Formik, Form as FormikForm } from 'formik';
import axios from 'axios';

import routes from 'app/routes';
import LoadingPlaceholder from 'components/new_ui/WaitLayers/LoadingPlaceholder';
import SubmitButton from 'components/Widgets/SubmitButton';
import useCareerPulseQuestions from 'components/Common/CareerPulseQuestions/Hooks/useCareerPulseQuestions';

import Field from './Field';

export default function Form({
  activeBlock, setActiveBlock, setProgress, onNextStep,
}) {
  const { programId, typeAssessment } = useParams();
  const navigate = useNavigate();
  const progressData = JSON.parse(localStorage.getItem('talentAssessmentProgressData') || '{}');
  const [selectedValues, setSelectedValues] = useState(Object.keys(progressData).length);
  const visibleBlocks = localStorage.getItem('talentAssessmentVisibleBlocks')?.split(',') || [activeBlock];

  const allQuestions = useCareerPulseQuestions();

  const [careerPulseQuestions, setCarerPulseQuestions] = useState(null);

  const careerPulseQuestionsGroupByBlock = careerPulseQuestions && (
    Object.groupBy(careerPulseQuestions, (question) => question.attributes.block)
  );

  const formSchema = Yup.object().shape({
  });

  const onSubmitHundler = (values, { setSubmitting, _setErrors }) => {
    const params = {
      career_pulse_assessment_report: values,
      program_id: programId,
      type_assessment: typeAssessment,
    };

    axios
      .post(routes.api.career_pulse_assessment_reports.create, params)
      .then((_resp) => {
        localStorage.removeItem('talentAssessmentProgressData');
        localStorage.removeItem('talentAssessmentVisibleBlocks');
        setSubmitting(false);

        if (onNextStep !== undefined) {
          onNextStep();
        } else if (programId && typeAssessment === 'first') {
          navigate(routes.members.programs.talentAssessmentsShow(programId, typeAssessment));
        } else if (programId && (typeAssessment === 'middle' || typeAssessment === 'last')) {
          navigate(routes.members.programs.growthReportShow(programId));
        } else {
          navigate(routes.members.talentAssessment.show);
        }
      });
  };

  const sleep = (ms) => new Promise((r) => { setTimeout(r, ms); });

  const scrollToNextQuestion = async (currentItem) => {
    const itemWrapper = currentItem.closest('.scrollable-item');
    const scrollableContent = itemWrapper.closest('.scrollable-content');
    const itemHeight = itemWrapper.getBoundingClientRect().height;

    itemWrapper.classList.add('disabled');

    let nextItem = itemWrapper.nextSibling;
    if (nextItem) {
      nextItem.classList.remove('disabled');

      if (nextItem.querySelector('button')) {
        nextItem.querySelector('button').classList.remove('disabled');
      }

      await sleep(50);
      scrollableContent.style.transform += `translateY(-${itemHeight}px)`;
    } else {
      const nextBlockWrapper = currentItem.closest('.block-wrapper').nextSibling;

      if (nextBlockWrapper) {
        nextItem = nextBlockWrapper.firstChild;

        setActiveBlock(nextItem.dataset.block);
        nextItem.classList.remove('disabled');
        scrollableContent.style.transform += `translateY(-${itemHeight}px)`;

        nextBlockWrapper.classList.remove('d-none');
      }
    }
  };

  useEffect(() => {
    if (allQuestions) {
      setCarerPulseQuestions(allQuestions.filter((item) => item.attributes.block !== 'block6'));
    }
  }, [allQuestions]);

  useEffect(() => {
    if (careerPulseQuestions) {
      // Set the progress bar.
      setProgress(parseInt((selectedValues * 100) / careerPulseQuestions.length, 10));

      // Move to current, active block.
      document.querySelectorAll('.block-wrapper').forEach((item) => {
        if (item.dataset.block !== activeBlock && visibleBlocks.includes(item.dataset.block)) {
          const scrollableContent = item.closest('.scrollable-content');
          const { height } = item.getBoundingClientRect();

          scrollableContent.style.transform += `translateY(-${height}px)`;
        }
      });

      // Move to current, non-answered question.
      const activeBlockWrapper = document.querySelector(`.block-wrapper[data-block="${activeBlock}"]`);
      activeBlockWrapper.querySelectorAll('.scrollable-item').forEach((item) => {
        const input = item.querySelector('.form-check-input, textarea');

        if (input && Object.keys(progressData).includes(input.name)) {
          scrollToNextQuestion(input);
        }
      });
    }
  }, [careerPulseQuestions]);

  return (
    <Formik
      validationSchema={formSchema}
      initialValues={progressData}
      onSubmit={onSubmitHundler}
    >
      {({ isSubmitting }) => (
        <FormikForm className="talent-assessment-form border-top border-light" noValidate="">
          {careerPulseQuestions ? (
            <section>
              <Row>
                <Col
                  lg={{ span: 10, offset: 1 }}
                  xl={{ span: 8, offset: 2 }}
                  className="scrollable-wrapper"
                >
                  <div className="scrollable-content">
                    {Object.keys(careerPulseQuestionsGroupByBlock).map((block) => (
                      <div
                        key={block}
                        data-block={block}
                        className={`block-wrapper form-with-counter ${visibleBlocks.includes(block) ? '' : 'd-none'}`}
                      >
                        {careerPulseQuestionsGroupByBlock[block].map((careerPulseQuestion, index) => (
                          <Field
                            key={careerPulseQuestion.id}
                            item={careerPulseQuestion}
                            active={block === activeBlock && index === 0}
                            afterClick={(target, value) => {
                              // Update the progress bar.
                              setProgress(parseInt(((selectedValues + 1) * 100) / careerPulseQuestions.length, 10));
                              setSelectedValues(selectedValues + 1);

                              // Save current progress in localStorage object.
                              const data = {
                                ...JSON.parse(localStorage.getItem('talentAssessmentProgressData') || '{}'),
                                [careerPulseQuestion.attributes.name]: value,
                              };
                              localStorage.setItem('talentAssessmentProgressData', JSON.stringify(data));

                              scrollToNextQuestion(target);
                            }}
                          />
                        ))}

                        {block !== 'block5' ? (
                          <div
                            className="scrollable-item pb-3 disabled"
                            data-block={block}
                          >
                            <Button
                              className="d-block mx-auto mb-4 disabled"
                              onClick={(e) => {
                                e.target.classList.add('disabled');
                                e.target.parentElement.parentElement.classList.add('disabled');

                                scrollToNextQuestion(e.target);
                              }}
                            >
                              Next Section
                            </Button>
                          </div>
                        ) : (
                          <div
                            className="text-center"
                            data-block="block5"
                          >
                            <SubmitButton
                              loading={isSubmitting}
                              disabled={selectedValues !== careerPulseQuestions.length}
                            >
                              Complete My Assessment
                            </SubmitButton>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </Col>
              </Row>
            </section>
          ) : (
            <section>
              <LoadingPlaceholder text="Loading..." />
            </section>
          )}
        </FormikForm>
      )}
    </Formik>
  );
}

Form.propTypes = {
  activeBlock: PropTypes.string.isRequired,
  setActiveBlock: PropTypes.func.isRequired,
  setProgress: PropTypes.func.isRequired,
  onNextStep: PropTypes.func,
};

Form.defaultProps = {
  onNextStep: undefined,
};
